import React, { useEffect, useMemo, useRef, useState } from 'react'
import { portal } from '../portalApi.js'
import { Icon } from '../Icons.jsx'

// 보고서 — 자금일보·손익계산서 화면에서 단추로 만든 것을 모아 둔다.
//
// 차림은 ERMORE OS 「리더 업무보고」의 **표 보기** 그대로다(받은 시안). 머리에 아이콘과
// 제목, 그 아래 보기 줄(표 보기 · 보기 추가 · 속성 · 보기 설정), 거르개 알약 줄, 그리고
// 여덟 칸짜리 표다. 경영자 페이지로 넘어갈 글이라 여기서 보던 모양과 저기서 보이는
// 모양이 다르면 무엇이 빠졌는지 매번 눈으로 맞춰 봐야 한다.
//
// 보기는 **표 보기 하나만** 둔다. 목록·현황별·보낸사람별처럼 미리 만들어 둔 보기는 없고,
// 사람이 「+ 보기」로 제 거르개와 칸을 담아 새 보기를 만든다(노션이 그렇다).
// 보기는 브라우저에 저장한다 - 보는 방식은 사람마다 달라서 남의 보기가 내 화면을
// 바꾸면 안 된다. 보고서 자체는 서버에 있으므로 보기를 잃어도 글은 그대로다.
//
// 표에서 바로 고칠 수 있는 것은 **구분과 상태** 둘뿐이다. 둘은 훑다가 바꾸는 칸이라
// 글을 열게 하면 번거롭고, 나머지(결재자·날짜)는 글을 열고 고친다.
//
// 결재는 우리가 돌리지 않는다. 칸을 채워 넘기고 승인은 경영자 페이지에서 한다.
// 그래서 결재자 두 칸은 상태가 아니라 그냥 이름이다.

const 종류이름 = { treasury: '자금일보', pnl: '손익계산서', note: '직접 작성' }
const 만드는곳 = [
  { key: 'treasury-daily', label: '자금일보', hint: '화면을 그대로 찍어 담습니다' },
  { key: 'closing-pnl', label: '손익계산서', hint: '연도·사업부를 고르고 만듭니다' },
]
const 기본마감 = 7        // 서버와 같은 값. 비워 두면 받는 쪽에서 되묻게 된다.
const 모든열 = ['사업부', '구분', '제목', '작성자', '1차결재자', '최종결재자', '게시일', '마감일', '상태']
const 열너비 = { 사업부: 92, 구분: 92, 작성자: 150, '1차결재자': 132, 최종결재자: 132, 게시일: 104, 마감일: 104, 상태: 104 }
const 사람칸 = new Set(['작성자', '1차결재자', '최종결재자'])
const 날짜칸 = new Set(['게시일', '마감일'])
// 시안의 색 그대로 - 구분은 바탕 알약, 상태는 점 달린 알약이다.
const 구분색 = { 자금: 'blue', 손익: 'purple', 정기: 'green', 기타: 'gray' }
// 사업부는 테두리만 있는 알약이다 - 구분과 나란히 서므로 둘 다 색을 가지면 어느 쪽이
// 무엇인지 안 읽힌다. 색으로 가르는 것은 구분 하나로 족하다.
const 상태색 = { 신규: 'gray', 진행중: 'blue', 결재요청: 'amber', 완료: 'green' }
const 고를것기본 = {
  사업부: ['전체', '에르모어', '링크맘', '더에르고'],
  구분: ['자금', '손익', '정기', '기타'],
  상태: ['신규', '진행중', '결재요청', '완료'],
}

// 보기에는 **숨긴 칸**만 적어 둔다. 보이는 칸을 적어 두면 나중에 칸을 더했을 때
// 예전에 저장해 둔 보기에는 그 칸이 영영 안 나타난다 - 사업부 칸이 실제로 그랬다.
const 기본보기 = { id: 'table', name: '표 보기', 구분: [], 상태: [], 숨긴열: [] }
const 보기자리 = 'fa-report-views'
const 보던보기 = 'fa-report-view'

function 보기읽기() {
  try {
    const v = JSON.parse(localStorage.getItem(보기자리) || 'null')
    if (Array.isArray(v) && v.length) {
      // 옛 꼴(보이는 칸 목록 `열`)은 버리고 전부 보이는 것으로 친다. 그때는 칸을 숨긴
      // 사람이 없었고, 그대로 두면 그 뒤에 더한 칸이 안 보인다.
      return v.map((x) => ({
        id: x.id, name: x.name,
        구분: x.구분 || [], 상태: x.상태 || [],
        숨긴열: (x.숨긴열 || []).filter((k) => 모든열.includes(k) && k !== '제목'),
      }))
    }
  } catch { /* 저장된 게 깨졌으면 기본 보기로 */ }
  return [기본보기]
}
const 보기쓰기 = (v) => { try { localStorage.setItem(보기자리, JSON.stringify(v)) } catch {} }

const 날짜 = (t) => String(t || '').slice(0, 10)
const 때 = (t) => String(t || '').slice(0, 16).replace('T', ' ')

/** 표 안에서 바로 고르는 알약. 누를 때 줄이 열리면 안 되니 눌림을 막는다. */
function 고르개({ 값, 고를것, 색, 점, 테두리, onChange }) {
  // 목록에 없는 값이 들어 있을 수 있다(이름이 바뀐 사업부 등). 그대로 두면 고르개가
  // 빈 칸으로 보여 «값이 없다»고 읽힌다 - 있는 값은 늘 보이게 끼워 넣는다.
  const 것들 = 값 && !고를것.includes(값) ? [값, ...고를것] : 고를것
  const 꼴 = 테두리 ? 'edge' : (값 ? (색[값] || 'gray') : 'none')
  return (
    <span className={`nv-pill ${값 ? 꼴 : 'none'}`} onClick={(e) => e.stopPropagation()}>
      {점 && 값 ? <i className="dot" /> : null}
      <select value={값 || ''} onChange={(e) => onChange(e.target.value)}>
        {!값 && <option value="">—</option>}
        {것들.map((v) => <option key={v} value={v}>{v}</option>)}
      </select>
    </span>
  )
}

function 사람({ 값 }) {
  if (!값) return <span className="muted">-</span>
  return <span className="nv-who"><i className="nv-ava">{값.trim().slice(0, 1)}</i>{값}</span>
}

/** 날짜 칸 — 평소엔 글자, 누르면 달력.
 *
 * date 칸을 그냥 두면 브라우저가 제 나라 꼴로 그린다(10/27/2026). 시안은 2026-10-27 이고,
 * 표에 선 날짜와도 꼴이 달라 같은 날이 두 가지로 보인다. */
function 날짜줄({ 값, onChange }) {
  const [고치는중, set고치는중] = useState(false)
  if (!고치는중) {
    return <span className="rp-flat rp-tap" onClick={() => set고치는중(true)}>{값 || '—'}</span>
  }
  return (
    <input type="date" value={값 || ''} autoFocus onBlur={() => set고치는중(false)}
      onChange={(e) => onChange(e.target.value)} />
  )
}

// 붙여넣기 — **서식을 살려 받는다.**
//
// 처음에는 글자만 받았다. 워드에서 오는 style 범벅을 서버가 어차피 벗겨 내니 모양이
// 깨질 바에 글자만 받자는 것이었는데, 실제로 옮기는 것은 *이미 표로 정리된 글*이었다
// (배당금 현황처럼 제목 + 표). 글자만 받으면 그걸 사람이 다시 표로 짜야 한다.
//
// 그래서 여기서도 서버와 **같은 규칙**으로 거른다 - 허용한 태그만 남기고, 속성은 벗기고,
// 남의 서버 그림은 버린다. 서버에서만 거르면 쓰는 동안 보이던 모양과 저장한 뒤 모양이
// 달라져 «왜 바뀌었나» 를 매번 따져 보게 된다.
const 붙일태그 = new Set([
  'P', 'BR', 'HR', 'DIV', 'SPAN', 'B', 'STRONG', 'I', 'EM', 'U', 'S', 'CODE', 'PRE',
  'H1', 'H2', 'H3', 'H4', 'H5', 'UL', 'OL', 'LI', 'BLOCKQUOTE', 'A',
  'TABLE', 'THEAD', 'TBODY', 'TR', 'TH', 'TD', 'IMG', 'FIGURE', 'FIGCAPTION',
])
const 우리그림 = /^\/api\/reports\/img\/[0-9a-f]{12}_up\.(png|jpg)$/
// 칸 맞춤은 style 을 들고 오는 대신 **보고서가 이미 쓰는 표시**로 옮긴다 - 숫자 칸 n,
// 가운데 c. style 을 남기면 거기에 색·글꼴·배경까지 따라 들어온다.
const 맞춤표시 = { right: 'n', center: 'c' }

function 서식걸러(들어온것) {
  const 틀 = new DOMParser().parseFromString(들어온것, 'text/html')
  틀.body.querySelectorAll('script,style,iframe,object,embed,noscript,meta,link')
    .forEach((e) => e.remove())
  let 버린그림 = 0
  for (const e of [...틀.body.querySelectorAll('img')]) {
    if (!우리그림.test(e.getAttribute('src') || '')) { 버린그림 += 1; e.remove() }
  }
  // 허용 안 된 태그는 벗기고 안의 글은 살린다. 벗기면 그 자리에 또 허용 안 된 것이
  // 드러날 수 있어 한 번 더 돈다(워드는 span 을 여남은 겹 쌓아 온다).
  for (let 돌이 = 0; 돌이 < 20; 돌이 += 1) {
    const 남은것 = [...틀.body.querySelectorAll('*')].filter((e) => !붙일태그.has(e.tagName))
    if (!남은것.length) break
    for (const e of 남은것) {
      const 자리 = e.parentNode
      if (!자리) continue
      while (e.firstChild) 자리.insertBefore(e.firstChild, e)
      e.remove()
    }
  }
  for (const e of [...틀.body.querySelectorAll('*')]) {
    const 표시 = 맞춤표시[(e.style && e.style.textAlign) || '']
      || ([...e.classList].includes('n') ? 'n' : '')
    const href = e.tagName === 'A' ? (e.getAttribute('href') || '') : ''
    const src = e.tagName === 'IMG' ? (e.getAttribute('src') || '') : ''
    const alt = e.tagName === 'IMG' ? (e.getAttribute('alt') || '') : ''
    const 칸수 = { colspan: e.getAttribute('colspan'), rowspan: e.getAttribute('rowspan') }
    for (const a of [...e.attributes]) e.removeAttribute(a.name)
    if (href && /^(https?:\/\/|mailto:|\/)/i.test(href)) e.setAttribute('href', href)
    if (src) { e.setAttribute('src', src); if (alt) e.setAttribute('alt', alt) }
    if (표시 && (e.tagName === 'TD' || e.tagName === 'TH')) e.setAttribute('class', 표시)
    for (const [k, v] of Object.entries(칸수)) {
      if (v && /^[1-9][0-9]?$/.test(v) && (e.tagName === 'TD' || e.tagName === 'TH')) {
        e.setAttribute(k, v)
      }
    }
  }
  return { 글: 틀.body.innerHTML, 버린그림 }
}

// 날짜는 **그 자리 시각**으로 만든다. toISOString() 은 UTC 라 아침 9시 전에는 어제가 된다.
const 그날 = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
const 오늘 = () => 그날(new Date())
const 날더하기 = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return 그날(d) }

// 서식 단추. 노션처럼 글 위에 줄로 선다 - 적다가 쓰는 것이라 메뉴 안에 숨기지 않는다.
const 서식들 = [
  { 글: '제목', cmd: 'formatBlock', 값: '<h4>' },
  { 글: '본문', cmd: 'formatBlock', 값: '<p>' },
  { 글: 'B', cmd: 'bold', 꼴: 'b' },
  { 글: 'I', cmd: 'italic', 꼴: 'i' },
  { 글: 'U', cmd: 'underline', 꼴: 'u' },
  { 글: '• 목록', cmd: 'insertUnorderedList' },
  { 글: '1. 목록', cmd: 'insertOrderedList' },
  { 글: '— 선', cmd: 'insertHorizontalRule' },
]
// 표는 보고서에서 자주 쓰므로 빈 표를 넣어 준다. 칸을 늘리는 것은 줄 끝에서 Tab 이 아니라
// 사람이 칸 안에서 Enter 로 하게 두었다 - 격자 편집기까지 만들 자리가 아니다.
const 빈표 = '<table><thead><tr><th>항목</th><th>내용</th></tr></thead><tbody>'
  + '<tr><td><br></td><td><br></td></tr><tr><td><br></td><td><br></td></tr>'
  + '</tbody></table><p><br></p>'

/** 보고서 쓰는 창 — 「+ 새 보고서」와 직접 쓴 보고서의 「글 고치기」가 같이 쓴다.
 *
 * 자금일보·손익 보고서는 화면 숫자를 굳혀 담으므로 글을 고칠 수 없다(그게 기록의 뜻이다).
 * 그런데 보고에는 숫자가 아닌 것도 있다 - 월말 소견, 특이사항, 회의 결과. 여태 그런 글은
 * 올릴 자리가 없어 메신저로 돌았고 경영자 페이지에 남지 않았다. 그래서 여기서 쓴다.
 *
 * 바탕을 눌러도 닫지 않는다. 한참 적다가 빗나간 한 번으로 글이 사라지면 다시 안 쓴다 -
 * 닫는 길은 「취소」와 ×뿐이고, 적은 것이 있으면 되묻는다.
 */
function 쓰는창({ 처음, 고를수있는, onClose, onSaved, goto }) {
  const 고치는것 = !!(처음 && 처음.id)
  const [제목, set제목] = useState((처음 && 처음.제목) || '')
  const [속성, set속성] = useState(() => ({
    사업부: (처음 && 처음.사업부) || '전체',
    구분: (처음 && 처음.구분) || '기타',
    작성자: (처음 && 처음.작성자) || '',
    '1차결재자': (처음 && 처음['1차결재자']) || '',
    최종결재자: (처음 && 처음.최종결재자) || '',
    게시일: 날짜(처음 && 처음.게시일) || 오늘(),
    마감일: 날짜(처음 && 처음.마감일) || 날더하기(기본마감),
    상태: (처음 && 처음.상태) || '신규',
  }))
  const [저장중, set저장중] = useState(false)
  const [오류, set오류] = useState('')
  const [알림글, set알림글] = useState('')
  const [올리는중, set올리는중] = useState(false)
  const [손댐, set손댐] = useState(false)
  const 몸 = useRef(null)
  const 그림고르개 = useRef(null)
  // 「그림」 단추를 누르면 파일 고르는 창이 뜨면서 커서가 풀린다 - 누를 때의 자리를
  // 들고 있다가 그 자리에 끼운다.
  const 누른자리 = useRef(null)

  useEffect(() => {
    // Enter 로 <div> 가 아니라 <p> 가 생기게. 그냥 두면 한 글에 두 가지 줄이 섞여
    // 줄 간격이 들쭉날쭉해진다(서버가 둘 다 받으므로 보이는 데서만 어긋난다).
    try { document.execCommand('defaultParagraphSeparator', false, 'p') } catch { /* 옛 브라우저 */ }
    if (몸.current) {
      몸.current.innerHTML = (처음 && 처음.본문) || ''
      몸.current.focus()
    }
  }, [])

  const 명령 = (cmd, 값) => {
    try { document.execCommand(cmd, false, 값) } catch { /* 지원 안 하면 그냥 넘긴다 */ }
    set손댐(true)
    if (몸.current) 몸.current.focus()
  }
  // 커서 자리를 들고 있다가 그림을 올린 뒤 그 자리에 넣는다. 올리는 동안(몇백 ms)
  // 커서가 풀리면 그림이 글 맨 앞이나 엉뚱한 칸에 들어간다.
  const 선택저장 = () => {
    const s = window.getSelection()
    if (!s || !s.rangeCount || !몸.current || !몸.current.contains(s.anchorNode)) return null
    return s.getRangeAt(0).cloneRange()
  }
  const 선택복원 = (자리) => {
    if (몸.current) 몸.current.focus()
    if (!자리) return
    const s = window.getSelection()
    s.removeAllRanges()
    s.addRange(자리)
  }

  /** 그림을 서버에 올리고 그 자리에 끼운다. 붙여넣기·끌어놓기·「그림」 단추가 같이 쓴다. */
  const 그림넣기 = async (파일들, 자리) => {
    set올리는중(true); set오류('')
    try {
      let 처음 = 자리
      for (const f of 파일들) {
        const r = await portal.upload('/api/reports/upload', {}, [f])
        선택복원(처음); 처음 = null        // 두 장째부터는 바로 앞 그림 뒤에 이어 붙는다
        명령('insertHTML', `<img src="${r.url}" alt=""><p><br></p>`)
      }
    } catch (e) {
      set오류(`그림을 올리지 못했습니다: ${e.message}`)
    } finally { set올리는중(false) }
  }
  const 고른그림 = (e) => {
    const 파일 = [...(e.target.files || [])].filter((f) => f.type.startsWith('image/'))
    e.target.value = ''                   // 같은 파일을 다시 골라도 바뀐 것으로 치게
    if (파일.length) 그림넣기(파일, 누른자리.current)
  }

  // 붙여넣기 — 그림이면 올리고, 서식이 있으면 걸러서 그대로 살린다.
  const 붙이기 = (e) => {
    const cd = e.clipboardData || window.clipboardData
    if (!cd) return
    const 그림 = [...(cd.files || [])].filter((f) => f.type.startsWith('image/'))
    if (그림.length) {                     // 캡처를 그대로 붙인 때
      e.preventDefault()
      그림넣기(그림, 선택저장())
      return
    }
    const 서식 = cd.getData ? cd.getData('text/html') : ''
    e.preventDefault()
    if (서식) {
      const { 글, 버린그림 } = 서식걸러(서식)
      명령('insertHTML', 글)
      set알림글(버린그림
        ? `그림 ${버린그림}장은 남의 서버에 있는 것이라 빠졌습니다 — 화면을 캡처해 붙여넣으세요`
        : '')
      return
    }
    명령('insertText', cd.getData ? cd.getData('text/plain') : '')
  }

  const 끌어옴 = (e) => {
    if (!(e.dataTransfer && [...(e.dataTransfer.items || [])]
      .some((i) => i.kind === 'file'))) return
    e.preventDefault()
  }
  const 떨굼 = (e) => {
    const 파일 = [...((e.dataTransfer || {}).files || [])].filter((f) => f.type.startsWith('image/'))
    if (!파일.length) return
    e.preventDefault()
    그림넣기(파일, 선택저장())
  }

  const 적은게있나 = () => (제목.trim() || 손댐
    || (몸.current ? 몸.current.innerText.trim() : ''))
  const 닫기 = () => {
    if (!고치는것 && 적은게있나() && !confirm('적은 내용을 버릴까요?')) return
    onClose()
  }

  const 저장 = async () => {
    const t = 제목.trim()
    if (!t) { set오류('제목을 적어 주세요'); return }
    const 본문 = 몸.current ? 몸.current.innerHTML : ''
    set저장중(true); set오류('')
    try {
      if (고치는것) {
        await portal.post(`/api/reports/${처음.id}`, { 제목: t, 본문, ...속성 })
        onSaved(처음.id, '고쳤습니다')
      } else {
        const r = await portal.post('/api/reports/new', { 제목: t, 본문, 속성 })
        onSaved(r.id, '보고서를 만들었습니다')
      }
    } catch (e) { set오류(e.message) } finally { set저장중(false) }
  }

  const 칸 = (k) => {
    if (고를수있는[k]) {
      return (
        <고르개 값={속성[k]} 고를것={고를수있는[k]} 점={k === '상태'} 테두리={k === '사업부'}
          색={k === '상태' ? 상태색 : 구분색}
          onChange={(v) => set속성((p) => ({ ...p, [k]: v }))} />
      )
    }
    if (날짜칸.has(k)) {
      // 글 한 장과 같은 날짜줄을 쓴다. date 칸을 늘 펴 두면 브라우저가 제 나라 꼴로
      // 그려(11/02/2026) 같은 날이 창과 글에서 다르게 보인다.
      return <날짜줄 값={속성[k] || ''}
        onChange={(v) => set속성((p) => ({ ...p, [k]: v }))} />
    }
    return (
      <input type="text" value={속성[k] || ''}
        placeholder={k === '작성자' ? '비우면 내 이름이 들어갑니다' : '비어 있음'}
        onChange={(e) => set속성((p) => ({ ...p, [k]: e.target.value }))} />
    )
  }

  return (
    <div className="modal-backdrop" onClick={(e) => e.stopPropagation()}>
      <div className="modal-panel rpw">
        <div className="modal-header">
          <h3>{고치는것 ? '보고서 고치기' : '새 보고서'}</h3>
          <button className="modal-close" onClick={닫기}>×</button>
        </div>

        <div className="modal-body rpw-body">
          <input className="rpw-title" value={제목} placeholder="제목 없음"
            onChange={(e) => set제목(e.target.value)} />

          <div className="rp-props rpw-props">
            {모든열.filter((k) => k !== '제목').map((k) => (
              <div className="rp-row" key={k}>
                <div className="rp-key">{k}</div>
                <div className="rp-val">{칸(k)}</div>
              </div>
            ))}
          </div>

          <div className="rpw-tools">
            {서식들.map((f) => (
              <button key={f.글} type="button" className={'rpw-tool' + (f.꼴 ? ' ' + f.꼴 : '')}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => 명령(f.cmd, f.값)}>{f.글}</button>
            ))}
            <button type="button" className="rpw-tool"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => 명령('insertHTML', 빈표)}>표</button>
            <button type="button" className="rpw-tool" disabled={올리는중}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { 누른자리.current = 선택저장(); 그림고르개.current.click() }}>
              {올리는중 ? '올리는 중…' : '그림'}
            </button>
            <input type="file" accept="image/*" multiple ref={그림고르개}
              style={{ display: 'none' }} onChange={고른그림} />
          </div>

          <div className="rpw-edit rp-body" contentEditable suppressContentEditableWarning
            ref={몸} onInput={() => set손댐(true)} onPaste={붙이기}
            onDragOver={끌어옴} onDrop={떨굼}
            data-empty="여기에 보고 내용을 적습니다" />

          <p className="rpw-note">
            제목·표·목록은 <b>붙여넣으면 그대로</b> 옵니다. 캡처는 <b>그냥 붙여넣거나</b>
            {' '}끌어다 놓으면 올라갑니다(「그림」 단추도 같습니다).
            색·글꼴 같은 꾸밈은 빠집니다 — 보고서 꼴로 다시 맞춥니다.
          </p>
          {알림글 && <p className="rpw-note warn">{알림글}</p>}
          {오류 && <div className="error-box">{오류}</div>}
        </div>

        <div className="modal-actions rpw-actions">
          {!고치는것 && (
            <div className="rpw-from">
              <span className="muted">화면 숫자를 그대로 담으려면</span>
              {만드는곳.map((m) => (
                <button key={m.key} type="button" className="rp-obtn flat"
                  onClick={() => goto && goto(m.key)}>{m.label} 화면 →</button>
              ))}
            </div>
          )}
          <button className="rp-obtn" onClick={닫기}>취소</button>
          <button className="nv-primary" disabled={저장중} onClick={저장}>
            {저장중 ? '저장 중…' : (고치는것 ? '저장' : '만들기')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function Reports({ goto }) {
  const [목록, set목록] = useState(null)
  const [메타, set메타] = useState(null)
  const [고른것, set고른것] = useState(null)
  const [본, set본] = useState(null)
  const [err, setErr] = useState('')
  const [toast, setToast] = useState('')
  const [보내는중, set보내는중] = useState(false)
  const [보기들, set보기들] = useState(보기읽기)
  const [보기id, set보기id] = useState(() => {
    try { return localStorage.getItem(보던보기) || 기본보기.id } catch { return 기본보기.id }
  })
  const [연창, set연창] = useState('')      // add · props · config
  const [새이름, set새이름] = useState('')
  const [쓰는중, set쓰는중] = useState(null)   // {} 면 새로 쓰기, 보고서면 그 글 고치기

  const 알림 = (m) => { setToast(m); setTimeout(() => setToast(''), 2600) }
  const 고를수있는 = 메타?.고를수있는 || 고를것기본

  const 불러오기 = () => {
    portal.get('/api/reports').then((r) => set목록(r.reports || []))
      .catch((e) => setErr(e.status === 403 ? '보고서 화면 권한이 없습니다.' : e.message))
    portal.get('/api/reports/meta').then(set메타).catch(() => {})
  }
  useEffect(불러오기, [])

  useEffect(() => {
    if (!고른것) { set본(null); return }
    portal.get(`/api/reports/${고른것}`).then(set본).catch((e) => setErr(e.message))
  }, [고른것])

  const 보기 = 보기들.find((v) => v.id === 보기id) || 보기들[0]
  const 열 = 모든열.filter((k) => !(보기.숨긴열 || []).includes(k))

  const 보기고치기 = (바뀐것) => {
    set보기들((l) => {
      const n = l.map((v) => (v.id === 보기.id ? { ...v, ...바뀐것 } : v))
      보기쓰기(n)
      return n
    })
  }
  const 보기고르기 = (id) => {
    set보기id(id); set연창('')
    try { localStorage.setItem(보던보기, id) } catch {}
  }
  const 보기만들기 = () => {
    const 이름 = (새이름 || '').trim()
    if (!이름) return
    const v = { id: 'v' + Date.now(), name: 이름, 구분: [...보기.구분],
                상태: [...보기.상태], 숨긴열: [...(보기.숨긴열 || [])] }
    const n = [...보기들, v]
    set보기들(n); 보기쓰기(n); set새이름(''); 보기고르기(v.id)
  }
  const 보기지우기 = () => {
    if (보기.id === 기본보기.id) return
    const n = 보기들.filter((v) => v.id !== 보기.id)
    set보기들(n); 보기쓰기(n); 보기고르기(n[0].id)
  }

  const 거르기토글 = (칸, 값) => {
    const 지금 = 보기[칸] || []
    보기고치기({ [칸]: 지금.includes(값) ? 지금.filter((x) => x !== 값) : [...지금, 값] })
  }
  const 열토글 = (k) => {
    if (k === '제목') return                 // 제목까지 숨기면 어느 줄인지 알 수 없다
    const 숨김 = 보기.숨긴열 || []
    보기고치기({ 숨긴열: 숨김.includes(k) ? 숨김.filter((x) => x !== k) : [...숨김, k] })
  }

  const 보이는것 = useMemo(() => (목록 || []).filter((r) => (
    (!(보기.구분 || []).length || 보기.구분.includes(r.구분 || '기타'))
    && (!(보기.상태 || []).length || 보기.상태.includes(r.상태 || '신규'))
  )), [목록, 보기])

  const 고치기 = async (id, 이름, 값) => {
    set목록((l) => (l || []).map((r) => (r.id === id ? { ...r, [이름]: 값 } : r)))
    set본((b) => (b && b.id === id ? { ...b, [이름]: 값 } : b))
    try { await portal.post(`/api/reports/${id}`, { [이름]: 값 }) } catch (e) { setErr(e.message) }
  }

  // 받는 쪽이 어떤 시스템이든 붙여넣기만 하면 되게, 속성과 본문을 한 덩어리로 만든다.
  // 본문의 표는 접어 두었어도 함께 복사된다(DOM 에 붙이지 않은 조각이라 숨은 글도 읽힌다).
  const 복사하기 = async () => {
    if (!본) return
    const 속성글 = ['제목', ...(메타?.속성 || 모든열.filter((k) => k !== '제목'))]
      .map((k) => `${k}: ${본[k] || ''}`).join('\n')
    const 임시 = document.createElement('div')
    임시.innerHTML = 본.본문 || ''
    try {
      await navigator.clipboard.writeText(`${속성글}\n\n${임시.innerText.trim()}`)
      알림('복사했습니다 — 경영자 페이지에 붙여넣으세요 (그림은 따로 저장하세요)')
    } catch { setErr('복사가 막혔습니다. 아래 내용을 직접 선택해 복사하세요.') }
  }

  const 보내기 = async () => {
    if (!본 || 보내는중) return
    set보내는중(true); setErr('')
    try {
      await portal.post(`/api/reports/${본.id}/send`, {})
      알림('경영자 페이지로 보냈습니다')
      불러오기()
      portal.get(`/api/reports/${본.id}`).then(set본).catch(() => {})
    } catch (e) {
      setErr(e.message + (메타 && !메타.보낼곳있음
        ? ' — 받는 곳 주소가 아직 없습니다. 그때까지는 「복사」로 옮기세요.' : ''))
    } finally { set보내는중(false) }
  }

  // 글을 쓰거나 고친 뒤. 새로 쓴 것은 바로 **그 글을 열어 준다** - 만들어 놓고 목록에서
  // 다시 찾게 하면 제대로 들어갔는지 확인하려고 한 번 더 누르게 된다.
  const 저장됨 = (id, 말) => {
    set쓰는중(null)
    불러오기()
    if (고른것 === id) portal.get(`/api/reports/${id}`).then(set본).catch(() => {})
    else set고른것(id)
    알림(말)
  }

  const 지우기 = async (r) => {
    if (!confirm(`'${r.제목}' 을 지울까요?`)) return
    try {
      await portal.del(`/api/reports/${r.id}`)
      if (고른것 === r.id) set고른것(null)
      불러오기(); 알림('지웠습니다')
    } catch (e) { setErr(e.message) }
  }

  if (err && !목록) return <><h2>보고서</h2><div className="error-box">{err}</div></>
  if (!목록) return <><h2>보고서</h2><div className="muted">불러오는 중…</div></>

  const 셀 = (r, k) => {
    if (k === '사업부') {
      return <고르개 값={r.사업부} 고를것={고를수있는.사업부} 색={{}} 테두리
        onChange={(v) => 고치기(r.id, '사업부', v)} />
    }
    if (k === '구분') {
      return <고르개 값={r.구분} 고를것={고를수있는.구분} 색={구분색}
        onChange={(v) => 고치기(r.id, '구분', v)} />
    }
    if (k === '상태') {
      return <고르개 값={r.상태} 고를것={고를수있는.상태} 색={상태색} 점
        onChange={(v) => 고치기(r.id, '상태', v)} />
    }
    if (k === '제목') {
      return (
        <span className="nv-title-cell">
          <i className="nv-doc"><Icon.files /></i>
          <b>{r.제목}</b>
          {r.보낸때 && <span className="badge green">보냄</span>}
        </span>
      )
    }
    if (사람칸.has(k)) return <사람 값={r[k]} />
    if (날짜칸.has(k)) return <span className="nv-date">{날짜(r[k]) || '-'}</span>
    return r[k] || '-'
  }

  // ── 글 한 장 (표에서 줄을 누르면 연다)
  if (본) {
    const 기준글 = 본.기준끝 && 본.기준끝 !== 본.기준시작
      ? `${본.기준시작} ~ ${본.기준끝}` : 본.기준시작
    const 그림수 = ((본.데이터 || {}).그림 || []).length
    return (
      <>
        <div className="nv-crumb">
          <button className="nv-back" onClick={() => set고른것(null)}>← 보고서</button>
          <i className="sep">›</i><span className="muted">{본.제목}</span>
        </div>
        <div className="panel rp-detail">
          <div className="rp-doc">
            <div className="rp-head">
              <span className="rp-emoji"><Icon.files /></span>
              <h3 className="rp-title">{본.제목}</h3>
              {본.종류 === 'note' && (
                <button className="rp-obtn" onClick={() => set쓰는중(본)}>글 고치기</button>
              )}
              <button className="rp-obtn" onClick={복사하기}>복사</button>
              <button className="rp-obtn danger" onClick={() => 지우기(본)}>삭제</button>
            </div>

            <div className="rp-props">
              {모든열.filter((k) => k !== '제목').map((k) => (
                <div className="rp-row" key={k}>
                  <div className="rp-key">{k}</div>
                  <div className="rp-val">
                    {고를수있는[k] ? (
                      <고르개 값={본[k]} 고를것={고를수있는[k]} 점={k === '상태'}
                        테두리={k === '사업부'} 색={k === '상태' ? 상태색 : 구분색}
                        onChange={(v) => 고치기(본.id, k, v)} />
                    ) : 날짜칸.has(k) ? (
                      <날짜줄 값={날짜(본[k])} onChange={(v) => 고치기(본.id, k, v)} />
                    ) : (
                      <input type="text" value={본[k] || ''} placeholder="비어 있음"
                        onChange={(e) => 고치기(본.id, k, e.target.value)} />
                    )}
                  </div>
                </div>
              ))}
              <div className="rp-row">
                <div className="rp-key">기준기간</div>
                <div className="rp-val"><span className="rp-flat">{기준글 || '—'}</span></div>
              </div>
            </div>

            <hr className="rp-div" />

            <div className="rp-send">
              <div className="rp-send-top">
                <span className={'rp-state' + (본.보낸때 ? ' on' : '')}>
                  <i className="dot" />{본.보낸때 ? '보냄' : '등록됨'}
                </span>
                <span className="rp-send-t">
                  <b>{때(본.보낸때 || 본.만든때)}</b>{' '}
                  {본.보낸때 ? '경영자 페이지로 보냄' : '만듦'}
                  <span className="muted">
                    {/* 직접 쓴 글에는 찍은 화면도 굳힌 표도 없다 - 「표 포함」이라 적어 두면
                        받는 쪽에서 안 보이는 표를 찾는다. */}
                    {' · '}{본.종류 === 'note' ? '직접 쓴 글'
                      : `${그림수 ? `화면 ${그림수}장` : '그림 없음'} · 표 포함`}
                    {본.보낸결과 ? ` · ${본.보낸결과}` : ''}
                  </span>
                </span>
                <button className="rp-obtn" disabled={보내는중} onClick={보내기}>
                  {보내는중 ? '보내는 중…' : '경영자 페이지로 보내기'}
                </button>
              </div>
              <div className="rp-strip">
                <span className="rp-tag">{종류이름[본.종류] || 본.종류}</span>
                <span className="rp-tag">{기준글}</span>
                <span className="rp-tag">{본.만든이이름 || '만든이 없음'}</span>
                <button className="rp-obtn flat" onClick={복사하기}>복사</button>
              </div>
              {메타 && !메타.보낼곳있음 && (
                <div className="rp-note">
                  받는 곳 주소가 아직 없어 <b>보내기는 막혀 있습니다</b>.
                  그때까지는 <b>복사</b>로 옮기세요 — 속성과 표가 함께 복사됩니다
                  (그림은 눌러 저장하세요).
                </div>
              )}
            </div>

            <div className="rp-content">
              <div className="rp-sec">내용</div>
              <div className="rp-body" dangerouslySetInnerHTML={{ __html: 본.본문 || '' }} />
            </div>
          </div>
        </div>
        {err && <div className="error-box" style={{ marginTop: 10 }}>{err}</div>}
        {쓰는중 && (
          <쓰는창 처음={쓰는중} 고를수있는={고를수있는} goto={goto}
            onClose={() => set쓰는중(null)} onSaved={저장됨} />
        )}
        {toast && <div className="toast">{toast}</div>}
      </>
    )
  }

  // ── 표 보기
  return (
    <div className="nv" onClick={() => set연창('')}>
      <div className="nv-top">
        <span className="nv-ico"><Icon.files /></span>
        <h2 className="nv-h">보고서</h2>
        <div className="nv-top-btns" onClick={(e) => e.stopPropagation()}>
          <button className="nv-primary" onClick={() => set쓰는중({})}>
            + 새 보고서
          </button>
          <button className="nv-ghost" onClick={() => {
            navigator.clipboard.writeText(window.location.href)
              .then(() => 알림('이 화면 주소를 복사했습니다')).catch(() => {})
          }}>링크 복사</button>
        </div>
      </div>

      <div className="nv-bar" onClick={(e) => e.stopPropagation()}>
        <div className="nv-views">
          {보기들.map((v) => (
            <button key={v.id} className={'nv-view' + (v.id === 보기.id ? ' active' : '')}
              onClick={() => 보기고르기(v.id)}>
              <Icon.chart />{v.name}
            </button>
          ))}
          <div className="nv-anchor">
            <button className="nv-mini" onClick={() => set연창(연창 === 'add' ? '' : 'add')}>+ 보기</button>
            {연창 === 'add' && (
              <div className="nv-pop">
                <div className="nv-pop-h">새 보기</div>
                <div className="nv-pop-note">지금 걸어 둔 거르개와 보이는 칸을 그대로 담습니다.</div>
                <div className="nv-pop-form">
                  <input value={새이름} placeholder="보기 이름 (예: 결재 대기)" autoFocus
                    onChange={(e) => set새이름(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && 보기만들기()} />
                  <button className="nv-primary" onClick={보기만들기}>만들기</button>
                </div>
              </div>
            )}
          </div>
          <span className="nv-sep" />
          <div className="nv-anchor">
            <button className="nv-mini" onClick={() => set연창(연창 === 'props' ? '' : 'props')}>속성</button>
            {연창 === 'props' && (
              <div className="nv-pop">
                <div className="nv-pop-h">표에 보일 칸</div>
                {모든열.map((k) => (
                  <label key={k} className="nv-pop-chk">
                    <input type="checkbox" checked={열.includes(k)} disabled={k === '제목'}
                      onChange={() => 열토글(k)} />
                    {k}{k === '제목' && <span className="muted">늘 보입니다</span>}
                  </label>
                ))}
              </div>
            )}
          </div>
          <div className="nv-anchor">
            <button className="nv-mini" onClick={() => set연창(연창 === 'config' ? '' : 'config')}>보기 설정</button>
            {연창 === 'config' && (
              <div className="nv-pop">
                <div className="nv-pop-h">{보기.name}</div>
                {보기.id === 기본보기.id ? (
                  <div className="nv-pop-note">
                    기본 보기는 이름을 바꾸거나 지울 수 없습니다.
                    거르개를 바꾼 뒤 <b>「+ 보기」</b>로 따로 만들어 두세요.
                  </div>
                ) : (
                  <>
                    <div className="nv-pop-form">
                      <input value={보기.name} onChange={(e) => 보기고치기({ name: e.target.value })} />
                    </div>
                    <button className="nv-pop-row danger" onClick={보기지우기}>이 보기 지우기</button>
                  </>
                )}
                <button className="nv-pop-row" onClick={() => 보기고치기({ 구분: [], 상태: [] })}>
                  거르개 모두 풀기
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="nv-right"><span className="muted">{보이는것.length}건</span></div>
      </div>

      <div className="nv-chips" onClick={(e) => e.stopPropagation()}>
        <button className={'nv-chip' + (!보기.구분.length && !보기.상태.length ? ' on' : '')}
          onClick={() => 보기고치기({ 구분: [], 상태: [] })}>전체</button>
        {고를수있는.구분.map((v) => (
          <button key={v} className={`nv-chip k-${구분색[v] || 'gray'}${보기.구분.includes(v) ? ' on' : ''}`}
            onClick={() => 거르기토글('구분', v)}>{v}</button>
        ))}
        <span className="nv-chip-sep" />
        {고를수있는.상태.map((v) => (
          <button key={v} className={`nv-chip dot d-${상태색[v] || 'gray'}${보기.상태.includes(v) ? ' on' : ''}`}
            onClick={() => 거르기토글('상태', v)}><i />{v}</button>
        ))}
      </div>

      {err && <div className="error-box" style={{ marginBottom: 10 }}>{err}</div>}

      <div className="panel nv-tablewrap">
        <table className="nv-table">
          <thead>
            <tr>
              {열.map((k) => (
                <th key={k} style={열너비[k] ? { width: 열너비[k] } : undefined}>{k}</th>
              ))}
              <th className="nv-actcol" />
            </tr>
          </thead>
          <tbody>
            {보이는것.length === 0 && (
              <tr><td colSpan={열.length + 1} className="c muted" style={{ padding: '28px 0' }}>
                {목록.length ? '거르개에 걸리는 보고서가 없습니다' : '아직 만든 보고서가 없습니다'}
              </td></tr>
            )}
            {보이는것.map((r) => (
              <tr key={r.id} onClick={() => set고른것(r.id)}>
                {열.map((k) => (
                  <td key={k} className={k === '제목' ? 'nv-title-td' : undefined}>{셀(r, k)}</td>
                ))}
                <td className="nv-act">
                  <button onClick={(e) => { e.stopPropagation(); set고른것(r.id) }}>열기</button>
                  <button className="danger" onClick={(e) => { e.stopPropagation(); 지우기(r) }}>삭제</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="muted nv-foot">
        <b>「+ 새 보고서」</b>는 글을 직접 쓰는 창을 띄웁니다. 자금일보·손익계산서는
        <b> 그 화면의 「보고서 만들기」</b>로 만드세요 — 그때의 숫자를 그대로 굳혀 담기
        때문에 나중에 글을 고칠 수 없습니다. 속성은 경영자 페이지 양식 그대로이고,
        <b> 결재는 그 페이지에서</b> 합니다.
      </p>
      {쓰는중 && (
        <쓰는창 처음={쓰는중} 고를수있는={고를수있는} goto={goto}
          onClose={() => set쓰는중(null)} onSaved={저장됨} />
      )}
      {toast && <div className="toast">{toast}</div>}
    </div>
  )
}
